import { resolveTimeZone } from '@galapagos/shared';
import { DateTime } from 'luxon';

/**
 * The legacy signup app's link-driven acquisition incentive. A campaign link
 * carries `incentive`, a start date `sd` and an end date `ed`, and the reminder
 * shows on the first-call booking step while today falls inside those dates.
 * Production only ever handled the Amazon gift card, matched exactly.
 */
export type LegacyIncentive = 'amazon-gift-card';

const LEGACY_INCENTIVES: readonly LegacyIncentive[] = ['amazon-gift-card'];

function isLegacyIncentive(value: string | null): value is LegacyIncentive {
  return LEGACY_INCENTIVES.some((incentive) => incentive === value);
}

// Production parsed with date-fns `yyyy-MM-dd`, which also accepts an unpadded
// month or day (`2026-1-5`); Luxon's `M` and `d` accept both forms the same way.
function calendarDay(raw: string | null, zone: string): DateTime | null {
  if (raw === null) return null;
  const day = DateTime.fromFormat(raw, 'yyyy-M-d', { zone });
  return day.isValid ? day : null;
}

/**
 * The incentive a link asks for, or null when it names none, names one
 * production never handled, or `instant` falls outside its dates. Both ends
 * are whole days in `zone`, inclusive, so the visitor's own calendar decides.
 */
export function activeLegacyIncentive(
  search: string,
  zone: string,
  instant: DateTime,
): LegacyIncentive | null {
  const params = new URLSearchParams(search);
  const incentive = params.get('incentive');
  if (!isLegacyIncentive(incentive)) return null;
  const start = calendarDay(params.get('sd'), zone);
  const end = calendarDay(params.get('ed'), zone);
  if (start === null || end === null) return null;
  const now = instant.setZone(zone);
  return start.startOf('day') <= now && now <= end.endOf('day') ? incentive : null;
}

/**
 * The visitor's zone. Before an account exists the browser is the only zone
 * source, so its reported name goes through the shared resolver, which supplies
 * the repo default for a missing or unsupported name. Only the name is read;
 * every conversion happens in Luxon.
 */
export function visitorTimeZone(deviceZone: string | null = readDeviceTimeZone()): string {
  return resolveTimeZone(deviceZone);
}

function readDeviceTimeZone(): string | null {
  try {
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return zone && zone.length > 0 ? zone : null;
  } catch {
    return null;
  }
}

/** The incentive for this visitor right now, judged in their own zone. */
export function currentLegacyIncentive(search: string): LegacyIncentive | null {
  const zone = visitorTimeZone();
  return activeLegacyIncentive(search, zone, DateTime.now().setZone(zone));
}
